import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CreditCard, ShieldCheck, Loader2, ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TestPagamento() {
  const [form, setForm] = useState({
    nome: "Test",
    cognome: "Live",
    email: "",
    indirizzo: "Via Test 1",
    citta: "Roma",
    cap: "00100",
    provincia: "RM",
    codice_fiscale: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handlePay = async () => {
    setError("");
    if (!form.email || !form.nome || !form.cognome) {
      setError("Compila almeno nome, cognome ed email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/checkout/create-session`,
        {
          package_id: "test-1euro",
          billing: {
            nome: form.nome,
            cognome: form.cognome,
            email: form.email,
            indirizzo: form.indirizzo,
            citta: form.citta,
            cap: form.cap,
            provincia: form.provincia.toUpperCase(),
            codice_fiscale: form.codice_fiscale || "TSTLIV80A01H501Z",
          },
          origin_url: window.location.origin,
        },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Errore avvio pagamento");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7ff] via-white to-[#eef2ff] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-8 border border-slate-200">
        <Link to="/" className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-[#102a6b] font-bold">
          <ArrowLeft className="h-3.5 w-3.5" /> Torna alla home
        </Link>

        <div className="mt-4 flex items-start gap-3">
          <div className="h-12 w-12 rounded-2xl bg-[#102a6b] text-white flex items-center justify-center">
            <CreditCard className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10.5px] font-extrabold uppercase tracking-wider">
              <AlertTriangle className="h-3 w-3" /> Pagina di test
            </div>
            <h1 className="mt-2 text-2xl font-extrabold text-[#0a1f55]">Pagamento di prova — 1 €</h1>
            <p className="text-[13px] text-slate-600 mt-1">
              Serve solo per verificare il flusso Stripe live con una carta reale (addebito di 1€). Riceverai la ricevuta PDF via email.
            </p>
          </div>
        </div>

        <div className="mt-7 rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2 text-[12.5px] text-emerald-900">
          <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            Pagamento sicuro con <strong>Stripe</strong>. Accetta Visa, Mastercard, Amex, Apple Pay, Google Pay.
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome *"><Input value={form.nome} onChange={onChange("nome")} /></Field>
            <Field label="Cognome *"><Input value={form.cognome} onChange={onChange("cognome")} /></Field>
          </div>
          <Field label="Email *"><Input type="email" value={form.email} onChange={onChange("email")} placeholder="dove inviare la ricevuta" /></Field>
          <Field label="Indirizzo"><Input value={form.indirizzo} onChange={onChange("indirizzo")} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Field label="Città"><Input value={form.citta} onChange={onChange("citta")} /></Field></div>
            <Field label="CAP"><Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value.replace(/\D/g, "").slice(0, 5) })} maxLength={5} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Provincia"><Input value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} className="uppercase" /></Field>
            <Field label="Codice Fiscale"><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} className="uppercase" placeholder="opzionale" /></Field>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-[12.5px] font-semibold">{error}</div>
        )}

        <Button
          onClick={handlePay}
          disabled={loading}
          data-testid="test-pay-btn"
          className="mt-6 w-full h-12 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold"
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Apertura pagamento…</>
          ) : (
            <><CreditCard className="h-4 w-4 mr-2" /> Paga 1 € con carta</>
          )}
        </Button>

        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3 text-[11.5px] text-slate-600 flex items-start gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            Dopo il pagamento sarai reindirizzato a <code className="bg-white px-1 rounded">/checkout/success</code> e riceverai la ricevuta in PDF all&apos;email indicata. Puoi chiedere il rimborso del test dal pannello Admin Stripe.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] font-semibold text-[#0a1f55]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
